import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Eye, Baby, TrendingUp } from 'lucide-react';
import { infantilAPI } from '../services/api';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35 } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.05 } } };
const FIELD_COLORS = ['#8E52AB', '#F59E0B', '#EC4899', '#3B82F6', '#10B981'];

export default function ClassOverviewPage() {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [students, setStudents] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [studentObs, setStudentObs] = useState({});
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([infantilAPI.classes(), infantilAPI.dashboard(), infantilAPI.reference()])
      .then(([c, d, r]) => { setClasses(c); setDashboard(d); setRef(r); setLoading(false); });
  }, []);

  useEffect(() => {
    if (!selectedClass) return;
    infantilAPI.classStudents(selectedClass).then(setStudents);
    infantilAPI.observations.list({ class_id: selectedClass, limit: 500 }).then(d => {
      const map = {};
      (d.items || []).forEach(o => {
        if (!map[o.student_id]) map[o.student_id] = { total: 0, fields: {} };
        map[o.student_id].total++;
        const fid = o.bncc_field_id || 'none';
        map[o.student_id].fields[fid] = (map[o.student_id].fields[fid] || 0) + 1;
      });
      setStudentObs(map);
    });
  }, [selectedClass]);

  const fields = ref?.bncc_fields || [];
  const classStats = dashboard?.class_stats || [];

  return (
    <motion.div className="space-y-6" initial="hidden" animate="visible" variants={stagger} data-testid="class-overview-page">
      <motion.div variants={fadeUp}>
        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Users className="h-5 w-5 text-[#64327D]" /> Turma 360</h1>
        <p className="text-sm text-slate-500 mt-1">Visão geral com heatmap BNCC e distribuição de evidências</p>
      </motion.div>

      {/* Class Selector */}
      <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
        <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)}
          className="w-full max-w-md h-10 rounded-xl border border-slate-200 px-3 text-sm" data-testid="class-selector">
          <option value="">Selecione a turma...</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </motion.div>

      {/* Class Stats Cards */}
      {!selectedClass && classStats.length > 0 && (
        <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {classStats.map((c, i) => (
            <div key={i} onClick={() => setSelectedClass(c.id)}
              className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer">
              <h3 className="text-sm font-bold text-slate-800">{c.name}</h3>
              <div className="flex items-center gap-4 mt-3">
                <div className="flex items-center gap-1.5"><Eye className="h-3.5 w-3.5 text-[#64327D]" /><span className="text-sm font-bold text-[#64327D]">{c.observations}</span><span className="text-xs text-slate-400">obs</span></div>
                <div className="flex items-center gap-1.5"><Baby className="h-3.5 w-3.5 text-[#2BB673]" /><span className="text-sm font-bold text-[#2BB673]">{c.students_observed}</span><span className="text-xs text-slate-400">observados</span></div>
              </div>
            </div>
          ))}
        </motion.div>
      )}

      {/* Heatmap */}
      {selectedClass && students.length > 0 && (
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm overflow-x-auto">
          <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#64327D]" /> Heatmap BNCC por Criança</h3>
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className="text-left py-2 px-3 text-slate-500 font-medium w-48">Criança</th>
                {fields.map((f, i) => (
                  <th key={i} className="text-center py-2 px-2 font-medium" style={{ color: f.color }}>
                    <div className="truncate max-w-[90px]" title={f.name}>{f.code}</div>
                  </th>
                ))}
                <th className="text-center py-2 px-3 text-slate-500 font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s, si) => {
                const obs = studentObs[s.id] || { total: 0, fields: {} };
                return (
                  <tr key={si} className="border-t border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/v2/infantil/crianca/${s.id}`)}>
                    <td className="py-2.5 px-3 font-medium text-slate-700">{s.name}</td>
                    {fields.map((f, fi) => {
                      const cnt = obs.fields[f.id] || 0;
                      const intensity = cnt === 0 ? 0 : Math.min(1, cnt / 5);
                      return (
                        <td key={fi} className="text-center py-2.5 px-2">
                          <div className="w-10 h-8 rounded-lg mx-auto flex items-center justify-center font-bold text-[11px]"
                            style={{
                              backgroundColor: cnt === 0 ? '#f1f5f9' : `${f.color}${Math.round(intensity * 60 + 15).toString(16).padStart(2, '0')}`,
                              color: cnt === 0 ? '#cbd5e1' : f.color,
                            }}>
                            {cnt}
                          </div>
                        </td>
                      );
                    })}
                    <td className="text-center py-2.5 px-3">
                      <span className="font-bold text-sm" style={{ color: obs.total > 0 ? '#2BB673' : '#cbd5e1' }}>{obs.total}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Field legend */}
          <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-slate-100">
            {fields.map((f, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: f.color }} />
                <span className="text-[10px] text-slate-500">{f.name}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Student Grid */}
      {selectedClass && students.length > 0 && (
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="text-sm font-bold text-slate-800 mb-4">Crianças da Turma</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {students.map((s, i) => {
              const obs = studentObs[s.id] || { total: 0 };
              return (
                <button key={i} onClick={() => navigate(`/v2/infantil/crianca/${s.id}`)}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-slate-50 transition-all border border-transparent hover:border-slate-200">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                    style={{ background: obs.total > 0 ? 'linear-gradient(135deg, #64327D, #8E52AB)' : '#e2e8f0', color: obs.total > 0 ? 'white' : '#94a3b8' }}>
                    {(s.name || '?')[0]}
                  </div>
                  <span className="text-xs font-medium text-slate-700 text-center leading-tight">{s.name?.split(' ').slice(0, 2).join(' ')}</span>
                  <span className="text-[10px] font-bold" style={{ color: obs.total > 0 ? '#2BB673' : '#cbd5e1' }}>{obs.total} obs</span>
                </button>
              );
            })}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
